import { useEffect, useState } from 'react'
import { App, Card, Col, InputNumber, Row, Space, Statistic, Tag, Typography } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'

import {
  downloadMaintenanceReportCsv,
  maintenanceReportsApi,
  type ReportFilters,
} from '@/features/maintenance/api'
import DataTable from '@/shared/components/DataTable'
import PageHeader from '@/shared/components/PageHeader'
import { usePlantId } from '@/shared/hooks/usePlantId'
import ReportFilterBar from './components/ReportFilterBar'
import MaintenanceStatusBadge from './components/MaintenanceStatusBadge'
import { useMasterLookups } from './components/useMasterLookups'

const { Text } = Typography

interface Row {
  machine_id: string | null
  machine_code: string | null
  machine_name: string | null
  department_id: string | null
  root_cause_id: string | null
  failure_reason: string | null
  repeat_count: number
  first_occurrence: string | null
  last_occurrence: string | null
  total_downtime_minutes: number
  total_cost: number
  last_technician_id: string | null
  current_health_score: number | null
  health_status: string | null
  under_observation: boolean
  suggested_action: string | null
}

/**
 * Spec §10 — Repeat breakdown / chronic machine report. Threshold control
 * lets the user reproduce the spec's "UNDER_OBSERVATION" mark for machines
 * over a configured repeat count.
 */
export default function RepeatBreakdownReportPage() {
  const { message } = App.useApp()
  const { plantId } = usePlantId()
  const { deptName } = useMasterLookups()
  const [filters, setFilters] = useState<ReportFilters>({
    plant_id: plantId,
    from_date: dayjs().subtract(90, 'day').format('YYYY-MM-DD'),
    to_date: dayjs().format('YYYY-MM-DD'),
    min_occurrences: 2,
  })
  useEffect(() => {
    if (filters.plant_id !== plantId) setFilters((f) => ({ ...f, plant_id: plantId }))
  }, [plantId, filters.plant_id])

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['report-repeat-bd', filters],
    enabled: !!filters.plant_id,
    queryFn: () => maintenanceReportsApi.repeatBreakdown(filters) as unknown as Promise<Row[]>,
  })
  const rows = data ?? []
  const underObs = rows.filter((r) => r.under_observation).length

  const [exporting, setExporting] = useState(false)
  const exportCsv = async () => {
    setExporting(true)
    try {
      await downloadMaintenanceReportCsv(
        '/maintenance/reports/repeat-breakdown', filters,
        `repeat-breakdown-${dayjs().format('YYYYMMDD-HHmm')}.csv`,
      )
    } catch (e) { message.error((e as Error).message ?? 'Export failed') }
    finally { setExporting(false) }
  }

  const columns = [
    { title: 'Machine', dataIndex: 'machine_code', key: 'mc', width: 130 },
    { title: 'Name', dataIndex: 'machine_name', key: 'n', ellipsis: true },
    {
      title: 'Dept', key: 'd', width: 140,
      render: (_: unknown, r: Row) => deptName(r.department_id),
    },
    { title: 'Failure reason', dataIndex: 'failure_reason', key: 'fr' },
    { title: 'Repeat count', dataIndex: 'repeat_count', key: 'rc', width: 120,
      render: (v: number) => <Text strong type={v >= 4 ? 'danger' : 'warning'}>{v}</Text>,
    },
    { title: 'First seen', dataIndex: 'first_occurrence', key: 'fo', width: 130,
      render: (v: string | null) => v ? dayjs(v).format('DD MMM') : '—' },
    { title: 'Last seen', dataIndex: 'last_occurrence', key: 'lo', width: 130,
      render: (v: string | null) => v ? dayjs(v).format('DD MMM') : '—' },
    { title: 'Downtime (min)', dataIndex: 'total_downtime_minutes', key: 'dt', width: 130 },
    { title: 'Cost', dataIndex: 'total_cost', key: 'c', width: 110,
      render: (v: number) => `₹ ${v.toFixed(2)}` },
    {
      title: 'Health', key: 'h', width: 150,
      render: (_: unknown, r: Row) => (
        <Space>
          {r.current_health_score != null && <Text>{r.current_health_score}</Text>}
          <MaintenanceStatusBadge status={r.health_status} />
        </Space>
      ),
    },
    {
      title: 'Flag', dataIndex: 'under_observation', key: 'uo', width: 150,
      render: (v: boolean) => v ? <Tag color="red">UNDER OBSERVATION</Tag> : null,
    },
    { title: 'Suggested action', dataIndex: 'suggested_action', key: 'sa', ellipsis: true },
    {
      title: 'Drilldown', key: 'dr', width: 140,
      render: (_: unknown, _r: Row) => (
        <Link to="/maintenance/machine-health">Machine history →</Link>
      ),
    },
  ]

  return (
    <div>
      <PageHeader
        title="Repeat Breakdown"
        subtitle="Same machine + same root cause occurring above threshold."
      />
      <ReportFilterBar
        filters={filters} onChange={setFilters} onRefresh={() => refetch()}
        onExport={exportCsv} exporting={exporting}
        show={{ dateRange: true, department: true }}
      />
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space>
          <Text>Min occurrences:</Text>
          <InputNumber
            min={2} max={20}
            value={filters.min_occurrences ?? 2}
            onChange={(v) => setFilters((f) => ({ ...f, min_occurrences: Number(v) || 2 }))}
          />
        </Space>
      </Card>
      <Row gutter={12} style={{ marginBottom: 12 }}>
        <Col xs={12} md={8}><Card><Statistic title="Chronic groups" value={rows.length} /></Card></Col>
        <Col xs={12} md={8}><Card><Statistic title="Under observation" value={underObs} valueStyle={{ color: underObs > 0 ? '#cf1322' : undefined }} /></Card></Col>
        <Col xs={24} md={8}>
          <Card><Text type="secondary">UNDER_OBSERVATION is set on a machine when its current health band is UNDER_OBSERVATION, CRITICAL or STOPPED. Refresh health from the Machine Health page.</Text></Card>
        </Col>
      </Row>
      <DataTable rowKey={(r: Row) => `${r.machine_id ?? 'm'}-${r.root_cause_id ?? 'r'}`}
        columns={columns} dataSource={rows} loading={isLoading} />
    </div>
  )
}
